import { useState } from "react";

interface RegionCluster {
  region: string;
  icon: string;
  nearestHub: string;
  hubRoute: string;
  subregions: {
    name: string;
    communities: string[];
    localContext: string;
  }[];
}

const regionClusters: RegionCluster[] = [
  {
    region: "South Florida",
    icon: "ri-sun-line",
    nearestHub: "Miami",
    hubRoute: "/cities/miami",
    subregions: [
      {
        name: "Miami-Dade Metro",
        communities: ["Miami", "Coral Gables", "Coconut Grove", "Pinecrest", "Key Biscayne", "Doral", "Hialeah", "Miami Springs", "Allapattah", "Little Havana", "Wynwood", "Brickell", "Design District"],
        localContext: "Miami is the largest South Florida hub with the highest concentration of passport photo locations and shipping centers. Coral Gables and Brickell residents have walkable access to UPS and FedEx offices. The Miami Passport Agency serves life-or-death emergencies and travel within 14 days.",
      },
      {
        name: "Miami Beach & Coastal",
        communities: ["Miami Beach", "South Beach", "North Beach", "Sunny Isles Beach", "Aventura", "Bal Harbour", "Bay Harbor Islands", "Surfside", "North Miami Beach"],
        localContext: "Coastal Miami-Dade communities rely on the Miami hub for expedited service. Sunny Isles and Aventura have excellent FedEx and UPS access along Collins Avenue. South Beach visitors needing emergency passports should call immediately — same-day courier pickup is available from Collins Avenue hotels.",
      },
      {
        name: "Broward County (Fort Lauderdale Area)",
        communities: ["Fort Lauderdale", "Hollywood", "Pembroke Pines", "Plantation", "Weston", "Coral Springs", "Parkland", "Deerfield Beach", "Pompano Beach", "Miramar", "Davie", "Tamarac", "Sunrise", "Lauderhill"],
        localContext: "Broward County is served by multiple hubs. Fort Lauderdale and Hollywood are the primary processing centers. Pembroke Pines and Weston have strong shipping infrastructure. The Miami Passport Agency in downtown Miami serves travelers with proof of international travel within 14 days — it is the nearest regional passport agency for all of South Florida.",
      },
    ],
  },
  {
    region: "Palm Beach County",
    icon: "ri-tree-line",
    nearestHub: "West Palm Beach",
    hubRoute: "/cities/west-palm-beach",
    subregions: [
      {
        name: "Palm Beach County South",
        communities: ["Boca Raton", "Delray Beach", "Boynton Beach", "Lake Worth", "Lantana", "Manalapan", "Ocean Ridge", "Gulf Stream", "Highland Beach"],
        localContext: "Boca Raton and Delray Beach are the main service hubs in southern Palm Beach County. Atlantic Avenue in Delray and Mizner Park in Boca both have full-service shipping centers. Boynton Beach residents are roughly equidistant to Boca Raton and West Palm Beach hubs.",
      },
      {
        name: "Palm Beach County Central & North",
        communities: ["West Palm Beach", "Palm Beach", "Palm Beach Gardens", "Jupiter", "Tequesta", "North Palm Beach", "Lake Park", "Riviera Beach", "Royal Palm Beach", "Wellington", "Loxahatchee", "Greenacres"],
        localContext: "West Palm Beach is the county's primary hub with Downtown Clematis Street shipping access. Wellington equestrian residents and Palm Beach Gardens golf communities are served through the Gardens hub. Jupiter is the northernmost point in Palm Beach County with reliable passport service.",
      },
    ],
  },
  {
    region: "Southwest Florida",
    icon: "ri-water-flash-line",
    nearestHub: "Naples",
    hubRoute: "/cities/naples",
    subregions: [
      {
        name: "Collier County (Naples & Marco Island)",
        communities: ["Naples", "Marco Island", "Golden Gate", "Immokalee", "Everglades City", "Chokoloskee", "Vanderbilt Beach", "Pelican Bay", "North Naples"],
        localContext: "Naples is the luxury travel hub of Southwest Florida with strong seasonal demand from snowbird residents. Marco Island is connected by the Jolley Bridge and has limited on-island shipping — most residents use the Naples hub. Southwest Florida International Airport (RSW) in Fort Myers is the closest international airport.",
      },
      {
        name: "Lee County (Fort Myers & Cape Coral)",
        communities: ["Fort Myers", "Cape Coral", "Bonita Springs", "Estero", "Fort Myers Beach", "Sanibel", "Captiva", "Lehigh Acres", "North Fort Myers", "Bokeelia"],
        localContext: "Fort Myers and Cape Coral are the twin hubs of Lee County. Cape Coral's canal system makes some addresses tricky for courier pickup — call ahead with your bridge and canal address. Sanibel and Captiva are barrier islands accessible only by causeway; island residents should mail documents or schedule pickup from the mainland side.",
      },
      {
        name: "Sarasota & Manatee County",
        communities: ["Sarasota", "Bradenton", "Lakewood Ranch", "Venice", "North Port", "Englewood", "Nokomis", "Longboat Key", "Siesta Key", "Lido Key", "Anna Maria Island", "Holmes Beach", "Palmetto", "Ellenton"],
        localContext: "Sarasota is the cultural coast hub with strong shipping infrastructure around the Ringling College area. Lakewood Ranch is a master-planned community with easy I-75 access. Venice and North Port are growing retirement communities with seasonal passport demand spikes. Longboat Key and Siesta Key residents typically use Sarasota mainland services.",
      },
    ],
  },
  {
    region: "Florida Keys",
    icon: "ri-anchor-line",
    nearestHub: "Key West",
    hubRoute: "/cities/key-west",
    subregions: [
      {
        name: "Upper Keys",
        communities: ["Key Largo", "Islamorada", "Tavernier", "Key Biscayne", "Plantation Key", "Windley Key", "Upper Matecumbe", "Lower Matecumbe"],
        localContext: "The Upper Keys are the closest to mainland Miami. Key Largo and Islamorada have post offices with passport acceptance services. The Overseas Highway (US-1) is the only route — traffic from Miami can add 1-2 hours during peak season. Most Upper Keys residents use the Miami hub for expedited courier service.",
      },
      {
        name: "Middle Keys",
        communities: ["Marathon", "Grassy Key", "Duck Key", "Long Key", "Conch Key", "Vaca Key", "Fat Deer Key"],
        localContext: "Marathon is the commercial center of the Middle Keys with the only real shipping infrastructure between Islamorada and Key West. Marathon residents should use Priority Mail Express from the Marathon post office or schedule a courier pickup from the Seven Mile Bridge area.",
      },
      {
        name: "Lower Keys",
        communities: ["Key West", "Stock Island", "Big Coppitt Key", "Sugarloaf Key", "Geiger Key", "Saddlebunch Keys", "Cudjoe Key", "Summerland Key", "Ramrod Key", "Little Torch Key", "Big Pine Key", "No Name Key", "Newfound Harbor"],
        localContext: "Key West is the southernmost hub and the only Keys location with dedicated expedited passport service. The Key West International Airport (EYW) has limited international flights — most Keys travelers connect through Miami. Because Key West is 3.5 hours from Miami by car, using a courier service that handles mainland logistics is essential for time-sensitive applications.",
      },
    ],
  },
  {
    region: "Central Florida",
    icon: "ri-building-line",
    nearestHub: "Orlando",
    hubRoute: "/cities/orlando",
    subregions: [
      {
        name: "Orlando Metro",
        communities: ["Orlando", "Winter Park", "Maitland", "Altamonte Springs", "Casselberry", "Winter Springs", "Oviedo", "Lake Mary", "Longwood", "Sanford", "Fern Park", "Union Park", "Azalea Park", "Conway", "Belle Isle", "Edgewood", "Pine Castle", "Hunters Creek", "Dr. Phillips", "Windermere", "Winter Garden", "Ocoee", "Gotha", "Apopka", "Plymouth", "Zellwood"],
        localContext: "Orlando is the central Florida hub and one of our busiest locations. The I-4 corridor connects all Orlando suburbs. Winter Park and Winter Garden are upscale communities with strong shipping access. Disney area residents in Celebration, Windermere, and Dr. Phillips have courier pickup available from resort-area hotels. The nearest regional passport agency is the Miami Passport Agency — travelers with proof of international travel within 14 days can schedule an appointment there.",
      },
      {
        name: "Osceola & Polk County",
        communities: ["Kissimmee", "Celebration", "Davenport", "Poinciana", "St. Cloud", "Harmony", "Lake Buena Vista", "Four Corners", "Lakeland", "Auburndale", "Haines City", "Lake Wales", "Winter Haven", "Bartow"],
        localContext: "Kissimmee and Celebration are the Disney-adjacent hubs. Davenport and Four Corners are growing commuter communities for Orlando workers. Polk County (Lakeland, Winter Haven) is a separate sub-market about 45 minutes southwest of Orlando. Residents in this area typically use the Orlando hub or the Lakeland post office for routine services.",
      },
    ],
  },
  {
    region: "Tampa Bay",
    icon: "ri-ship-line",
    nearestHub: "Tampa",
    hubRoute: "/cities/tampa",
    subregions: [
      {
        name: "Hillsborough County (Tampa Area)",
        communities: ["Tampa", "Riverview", "Brandon", "Apollo Beach", "Gibsonton", "Seffner", "Thonotosassa", "Lutz", "Tampa Palms", "New Tampa", "Westshore", "Hyde Park", "Ybor City", "Davis Islands", "Bayshore", "Carrollwood", "Northdale", "Town 'n' Country", "Westchase", "Palm River", "East Tampa"],
        localContext: "Tampa is the Gulf Coast hub with Tampa International Airport (TPA) as the primary international departure point. Riverview and Brandon are fast-growing suburbs on the I-75 corridor. Westshore and Hyde Park have the best shipping infrastructure. The downtown Tampa passport acceptance facility is one of the busiest in the state — appointments book 2-3 weeks out.",
      },
      {
        name: "Pinellas County (St. Pete & Clearwater)",
        communities: ["St. Petersburg", "Clearwater", "Dunedin", "Palm Harbor", "Safety Harbor", "Largo", "Pinellas Park", "Seminole", "Gulfport", "South Pasadena", "Treasure Island", "Madeira Beach", "Redington Beach", "Indian Rocks Beach", "Belleair", "Belleair Beach", "Indian Shores", "North Redington Beach", "Redington Shores", "St. Pete Beach"],
        localContext: "Pinellas County is served by the Tampa hub with strong local shipping infrastructure. Clearwater and St. Petersburg have the most passport photo locations. Beach communities from St. Pete Beach to Clearwater Beach are all within 20 minutes of a full-service hub. The Sunshine Skyway Bridge connects Pinellas to Bradenton and Sarasota.",
      },
      {
        name: "Pasco & Hernando County",
        communities: ["Wesley Chapel", "Land O' Lakes", "Zephyrhills", "Dade City", "New Port Richey", "Port Richey", "Hudson", "Spring Hill", "Brooksville", "Weeki Wachee"],
        localContext: "Wesley Chapel is the fastest-growing area in Pasco County with I-75 access to Tampa in 25 minutes. Land O' Lakes and Zephyrhills are commuter communities. Hernando County (Spring Hill, Brooksville) is more rural and typically uses the Tampa hub or the Brooksville post office for routine acceptance.",
      },
    ],
  },
  {
    region: "North Florida",
    icon: "ri-tree-line",
    nearestHub: "Jacksonville",
    hubRoute: "/cities/jacksonville",
    subregions: [
      {
        name: "Jacksonville Metro",
        communities: ["Jacksonville", "Jacksonville Beach", "Atlantic Beach", "Neptune Beach", "Ponte Vedra Beach", "Ponte Vedra", "St. Johns", "Nocatee", "Fruit Cove", "Orange Park", "Middleburg", "Fleming Island", "Green Cove Springs", "Macclenny", "Yulee", "Fernandina Beach", "Amelia Island", "Callahan", "Baldwin", "Hilliard"],
        localContext: "Jacksonville is the largest city by land area in the contiguous US, so 'local' service varies by neighborhood. Ponte Vedra Beach and the Beaches communities are the most affluent area with strong shipping infrastructure. Orange Park and Fleming Island are the primary suburban hubs on the west side of the St. Johns River. The nearest regional passport agency is the Atlanta Passport Agency — northeast Florida travelers with emergency needs can also reach the Miami Passport Agency.",
      },
      {
        name: "Gainesville & North Central",
        communities: ["Gainesville", "Ocala", "Lake City", "Alachua", "Newberry", "High Springs", "Archer", "Williston", "Micanopy", "McIntosh", "Hawthorne", "Interlachen", "Palatka", "Starke", "Keystone Heights", "Melrose"],
        localContext: "Gainesville is anchored by the University of Florida, creating a unique market of study-abroad students and international faculty. Ocala is the horse country hub 35 minutes south. Lake City is the I-10 crossroads town. The Gainesville hub serves a wide rural catchment area including Putnam and Bradford counties.",
      },
      {
        name: "St. Augustine & St. Johns County",
        communities: ["St. Augustine", "St. Augustine Beach", "Vilano Beach", "Crescent Beach", "Elkton", "Hastings", "Armstrong", "Julington Creek", "Durbin Crossing", "World Golf Village", "Murabella"],
        localContext: "St. Augustine is the nation's oldest city and a major tourist destination. The historic district has limited parking, so shipping from a suburban UPS location is often easier than downtown. World Golf Village is a master-planned community with easy I-95 access to Jacksonville. Crescent Beach and Vilano Beach are coastal communities with barrier island access.",
      },
    ],
  },
  {
    region: "Florida Panhandle",
    icon: "ri-drop-line",
    nearestHub: "Panama City",
    hubRoute: "/cities/panama-city",
    subregions: [
      {
        name: "Emerald Coast (Panama City to Destin)",
        communities: ["Panama City", "Panama City Beach", "Upper Grand Lagoon", "Lynn Haven", "Callaway", "Parker", "Springfield", "Destin", "Fort Walton Beach", "Niceville", "Valparaiso", "Shalimar", "Crestview", "Mary Esther", "Navarre", "Gulf Breeze", "Pensacola Beach", "Perdido Key", "Santa Rosa Beach", "Rosemary Beach", "Seaside", "Alys Beach", "Watercolor", "Grayton Beach", "Blue Mountain Beach"],
        localContext: "The Emerald Coast is a major vacation destination with high seasonal passport demand. Panama City and Panama City Beach are the eastern hub. Destin and Fort Walton Beach are the western hub. The 30A corridor (Seaside, Rosemary Beach, Alys Beach) is a string of upscale beach communities with limited local services — most residents use the Panama City or Destin hubs. Pensacola Beach and Perdido Key are barrier islands with causeway access.",
      },
      {
        name: "Pensacola & Western Panhandle",
        communities: ["Pensacola", "Pace", "Milton", "Cantonment", "Molino", "McDavid", "Walnut Hill", "Jay", "Century", "Brewton AL", "Atmore AL"],
        localContext: "Pensacola is the westernmost Florida city and the Panhandle hub closest to Alabama. The city has a strong military presence from Naval Air Station Pensacola, creating steady demand for overseas deployment passports. Pace and Milton are growing suburbs on the north side of Pensacola Bay. The Alabama border communities of Brewton and Atmore often use the Pensacola hub for convenience.",
      },
    ],
  },
  {
    region: "Florida Nature Coast",
    icon: "ri-leaf-line",
    nearestHub: "Cedar Key",
    hubRoute: "/cities/cedar-key",
    subregions: [
      {
        name: "Big Bend & Nature Coast",
        communities: ["Cedar Key", "Chiefland", "Bronson", "Williston", "Crystal River", "Inverness", "Beverly Hills", "Homosassa", "Homosassa Springs", "Floral City", "Dunnellon", "Yankeetown", "Fanning Springs", "Manatee Springs", "Suwannee", "Cross City", "Old Town", "Steinhatchee", "Horseshoe Beach", "Fowler's Bluff"],
        localContext: "The Nature Coast is one of Florida's most rural and beautiful regions. Cedar Key is a historic fishing village on the Gulf with limited infrastructure — most residents use the Gainesville hub (45 minutes east) or the Chiefland post office. Crystal River is the largest town in the area with the best shipping access. Steinhatchee and Horseshoe Beach are tiny Gulf communities with only USPS service. This area is popular with nature photographers and birdwatchers traveling internationally.",
      },
    ],
  },
  {
    region: "Tallahassee & Capital",
    icon: "ri-government-line",
    nearestHub: "Tallahassee",
    hubRoute: "/cities/tallahassee",
    subregions: [
      {
        name: "Leon & Surrounding Counties",
        communities: ["Tallahassee", "Florida State University", "Florida A&M University", "Killearn", "Killearn Estates", "Miccosukee", "Wacissa", "Crawfordville", "Woodville", "Fort Braden", "Bailey", "Havana", "Quincy", "Gretna", "Chattahoochee", "Midway", "Monticello", "Madison", "Perry", "Mayo"],
        localContext: "Tallahassee is the state capital and a major university town. Florida State and FAMU students create high seasonal demand for study-abroad passports. The Apalachee Parkway and Monroe Street corridors have the best shipping infrastructure. Quincy is a historic town 20 minutes west with its own dedicated passport page. The I-10 corridor connects Tallahassee to Jacksonville (2.5 hours east) and Pensacola (2 hours west).",
      },
    ],
  },
];

export default function FloridaCommunitiesWeServe() {
  const [openRegion, setOpenRegion] = useState<string>("South Florida");

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Statewide Coverage
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Communities We Serve Across Florida
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            From Miami Beach to the Panhandle, Fast Passport Center covers every corner of
            Florida. Find your community and the nearest service hub.
          </p>
        </div>

        {/* Region tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {regionClusters.map((cluster) => {
            const isActive = openRegion === cluster.region;
            return (
              <button
                key={cluster.region}
                onClick={() => setOpenRegion(cluster.region)}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  isActive
                    ? "bg-[#1A365D] text-white"
                    : "bg-[#F7F8FA] text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200"
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cluster.icon} text-sm`}></i>
                </span>
                {cluster.region}
              </button>
            );
          })}
        </div>

        {/* Active region content */}
        {regionClusters.map((cluster) => {
          if (cluster.region !== openRegion) return null;
          return (
            <div key={cluster.region} className="animate-fade-in">
              {/* Hub banner */}
              <div className="bg-[#0F2240] rounded-lg p-6 md:p-8 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-white text-lg mb-1">
                    Primary Service Hub: {cluster.nearestHub}
                  </h3>
                  <p className="text-white/60 text-sm">
                    All {cluster.region} communities are served through the {cluster.nearestHub}{" "}
                    location with courier pickup and overnight shipping available.
                  </p>
                </div>
                <a
                  href={cluster.hubRoute}
                  className="inline-flex items-center gap-1.5 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Visit {cluster.nearestHub} Page
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              {/* Subregion grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {cluster.subregions.map((sub, i) => (
                  <div
                    key={i}
                    className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/40 transition-all"
                  >
                    <h4 className="font-bold text-[#1A365D] text-base mb-3">
                      {sub.name}
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {sub.communities.map((comm, j) => (
                        <a
                          key={j}
                          href={cluster.hubRoute}
                          className="inline-flex items-center bg-white border border-gray-200 rounded-full px-3 py-1 text-xs text-[#1A365D] hover:border-[#F6C344] hover:text-[#F6C344] transition-all whitespace-nowrap"
                        >
                          <span className="w-3.5 h-3.5 flex items-center justify-center mr-1">
                            <i className="ri-map-pin-line text-[10px]"></i>
                          </span>
                          {comm}
                        </a>
                      ))}
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {sub.localContext}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Bottom note */}
        <div className="mt-10 bg-[#FEF9E7] border border-[#F6C344]/20 rounded-lg p-5 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-[#F6C344] text-lg"></i>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            <strong className="text-[#1A365D]">Do not see your town?</strong>{" "}
            We serve every address in Florida. If your community is not listed above, the nearest
            hub still covers you — just call{" "}
            <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">
              877-253-0084
            </a>{" "}
            and we will route your application to the closest processing center.
          </p>
        </div>
      </div>
    </section>
  );
}